import {getIntegrationHost} from "@/utils/integration-host";
import Scene = Phaser.Scene;
import {Keys} from "@/constants/key";
import Sprite = Phaser.GameObjects.Sprite;
import SpriteUtils from "@/utils/sprite-utils";
import PlayerStore from "@/store/player";
import QualityUtils from "@/utils/quality-utils";
import VkInsets from "@/utils/vk-insets";
import FrameViewport from "@/utils/frame-viewport";

export default class CU {

    public static WINDOW_RESIZED: boolean = false;

    public static readonly ETALON_CANVAS_WIDTH: number = 600;

    public static readonly MIN_DESKTOP_RATIO: number = 1.4;

    // На сколько CSS px айфрейм ОК может быть шире экрана, чтобы считать это срезом (см. visibleWidth).
    private static readonly OK_OVERFLOW_MIN: number = 20;
    private static readonly OK_OVERFLOW_MAX: number = 48;

    // The capture entry has a fixed viewport and starts without a player session.
    // Avoid querying player quality while its module graph is still initializing.
    private static readonly CAPTURE_VIEWPORT = window.location.pathname.endsWith('/purchase-capture.html');

    public static CANVAS_WIDTH: number = CU.CAPTURE_VIEWPORT ? 640 : CU.windowWidth();
    public static CANVAS_HEIGHT: number = CU.CAPTURE_VIEWPORT ? 920 : CU.windowHeight();

    public static CSS_WIDTH: number = CU.CAPTURE_VIEWPORT ? 320 : CU.cssWidth();
    public static CSS_HEIGHT: number = CU.CAPTURE_VIEWPORT ? 460 : CU.cssHeight();

    public static S: number = CU.CANVAS_WIDTH / CU.ETALON_CANVAS_WIDTH;

    public static CSS_S: number = CU.CSS_WIDTH / CU.ETALON_CANVAS_WIDTH;

    private static wall: Sprite | undefined;

    public static scale(num: number) {
        return num * CU.S;
    }

    public static shiftLeft(x: number) {
        return x * CU.S;
    }

    public static shiftTop(x: number) {
        return x * CU.S;
    }

    public static shiftBottom(canvas: HTMLCanvasElement, y: number) {
        return canvas.height - y * CU.S;
    }

    public static shiftRight(x: number) {
        return CU.CANVAS_WIDTH - x * CU.S;
    }

    public static blockCanvas(scene: Scene) {
        CU.wall = SpriteUtils.addSprite(scene!, 0, 0, Keys.ATLAS_UI, Keys.UI_BLACK).setOrigin(0, 0)
            .setDepth(1000000).setAlpha(1).setInteractive();
        SpriteUtils.setDispSize(CU.wall, CU.CANVAS_WIDTH, 2000);
    }

    public static isCanvasBlocked(): boolean {
        return !!CU.wall;
    }

    public static unblockCanvas() {
        if (CU.wall) {
            CU.wall.destroy();
            CU.wall = undefined;
        }
    }

    public static setCanvasBlockerCustomDepth(depth: number) {
        if (CU.wall) {
            CU.wall.setDepth(depth);
        }
    }

    /**
     * Допуск в пикселях: viewHeight() отдаёт дробную высоту, и одно только округление
     * в cssHeight() уже даёт расхождение в пиксель. Перестраивать из-за него сцену не нужно.
     */
    private static readonly SIZE_TOLERANCE: number = 2;

    /**
     * Видимая область не совпадает с размерами, под которые построен канвас.
     * Событие resize можно вообще не получить (панель встроенного браузера доезжает во время
     * загрузки, мини-апп разворачивается, Capacitor уходит в полный экран уже после launch)
     * или потерять - поэтому сверяемся с вьюпортом напрямую, а не только по флагу.
     */
    public static canvasSizeOutdated(): boolean {
        return Math.abs(CU.CSS_WIDTH - CU.cssWidth()) >= CU.SIZE_TOLERANCE
            || Math.abs(CU.CSS_HEIGHT - CU.cssHeight()) >= CU.SIZE_TOLERANCE;
    }

    /**
     * Returns true if canvas RESIZED and scene needs to be reloaded
     */
    public static checkCanvasSize(scene: Scene, reloadScene: () => unknown): boolean {
        if (CU.WINDOW_RESIZED || CU.canvasSizeOutdated()) {
            CU.WINDOW_RESIZED = false;
            CU.resizeCanvas('game-container');
            scene.game.scale.resize(CU.CANVAS_WIDTH, CU.CANVAS_HEIGHT);
            reloadScene();
            return true;
        }
        return false;
    }

    public static forceResizeCanvas() {
        CU.resizeCanvas('game-container');
    }

    public static resizeCanvas(containerId: string) {
        CU.CANVAS_WIDTH = CU.windowWidth();
        CU.CANVAS_HEIGHT = CU.windowHeight();

        CU.CSS_WIDTH = CU.cssWidth();
        CU.CSS_HEIGHT = CU.cssHeight();

        //PlayerStore.logClientError('CANV.W = ' + CU.CANVAS_WIDTH + '; CANV.H = ' + CU.CANVAS_HEIGHT + '; CSS.W = ' + CU.CSS_WIDTH + '; CSS.H = ' + CU.CSS_HEIGHT);

        CU.S = CU.CANVAS_WIDTH / CU.ETALON_CANVAS_WIDTH;

        const body = document.body;
        const canvasParrent = <HTMLElement> document.getElementById(containerId);

        body.style.width = String(CU.CSS_WIDTH) + 'px';
        body.style.height = String(CU.CSS_HEIGHT) + 'px';

        canvasParrent.style.width = String(CU.CSS_WIDTH) + 'px';
        canvasParrent.style.height = String(CU.CSS_HEIGHT) + 'px';

        let col: HTMLCollectionOf<HTMLCanvasElement> = document.getElementsByTagName('canvas');
        if (col && col.length > 0) {
            const canvas = col.item(0);
            if (canvas) {
                canvas.style.width = String(CU.CSS_WIDTH) + 'px';
                canvas.style.height = String(CU.CSS_HEIGHT) + 'px';
            }
        }
    }

    public static windowWidth() {
        if (CU.isDesktop()) { // DESKTOP:

            // LOW/LOWEST режут и десктопный канвас (500/450 против обычных 600):
            // на слабых машинах разрешение - главный рычаг производительности,
            // и на WebGL со слабым GPU, и на canvas-фоллбеке. Для STANDARD/HIGH
            // рекомендованная ширина >= 600 и ничего не меняет.
            return Math.min(window.visualViewport.width, this.getMaxWidthDesktop(), QualityUtils.getReccommendedWidth());

        } else { // MOBILE:

            return QualityUtils.getReccommendedWidth();
        }
    }

    public static windowHeight() {
        if (CU.isDesktop()) { // DESKTOP:

            // Ширина могла быть срезана качеством ниже CSS-ширины - высоту масштабируем
            // пропорционально, чтобы канвас растягивался браузером без искажения.
            // Без среза отношение равно 1 и высота остаётся высотой окна, как раньше.
            return Math.floor(CU.windowWidth() / CU.cssWidth() * CU.cssHeight());

        } else { // MOBILE:

            let width = CU.windowWidth();
            return Math.floor(width / CU.cssWidth() * CU.cssHeight());
        }
    }

    public static cssWidth() {
        if (CU.isDesktop()) { // DESKTOP:

            return Math.min(window.visualViewport.width, this.getMaxWidthDesktop());

        } else { // MOBILE:

            return CU.visibleWidth();
        }
    }

    public static cssHeight() {
        return Math.floor(CU.viewHeight());
    }

    public static virtualWindowHeight() {
        if (CU.CAPTURE_VIEWPORT) return Math.ceil(CU.CANVAS_HEIGHT / CU.S);
        return Math.ceil(CU.windowHeight() / CU.S);
    }

    public static isDesktop() {
        return FrameViewport.limit(window.visualViewport.height) < CU.visibleWidth() * 1.4;
    }

    /**
     * Полная версия ОК (ok.ru/game, не m.ok.ru) в мобильном браузере иногда верстает страницу
     * шире экрана ровно на 31-32 CSS px - вместе со своей шапкой и нашим айфреймом - и
     * показывает середину: по 16 px с каждого края за экраном (AOF-3099). Изнутри айфрейма
     * размеры при этом сходятся, видно расхождение только с screen.width. Канвас берём по
     * ширине экрана, по центру его ставит margin: 0 auto у body.
     * Большой перелёт (режим «версия для ПК», страница отдалена) не трогаем - там видно всё.
     * Пороги OK_OVERFLOW_* объявлены в начале класса: размеры считаются ещё при его загрузке.
     */
    private static visibleWidth(): number {
        const width = window.visualViewport.width;
        if (!CU.isOkFrame()) {
            return width;
        }
        const screenWidth = window.screen ? window.screen.width : 0;
        const overflow = width - screenWidth;
        return overflow >= CU.OK_OVERFLOW_MIN && overflow <= CU.OK_OVERFLOW_MAX ? screenWidth : width;
    }

    public static okOverflow(): number {
        return CU.isOkFrame() ? Math.round(window.visualViewport.width - CU.visibleWidth()) : 0;
    }

    // IntegrationUtils.isOkUser() отсюда не позвать: он сам импортирует CU, а размеры
    // считаются ещё при загрузке модуля.
    private static isOkFrame(): boolean {
        const host = getIntegrationHost();
        return (host.includes('ok.zazer.mobi') || host.includes('otest.zazer')) && window.top !== window.self;
    }

    private static getMaxWidthDesktop() {
        return Math.min(600, Math.floor(FrameViewport.limit(window.visualViewport.height) / CU.MIN_DESKTOP_RATIO));
    }

    private static viewHeight(): number {
        const visual = FrameViewport.limit(window.visualViewport.height);
        if (FrameViewport.getVisible() > 0 || !CU.shouldFillVkLayoutViewport()) {
            return visual;
        }
        const layout = Math.max(window.innerHeight || 0, document.documentElement?.clientHeight || 0);
        const height = layout > visual ? layout : visual;
        // Взять большую из двух высот мало: в VK на Android нижняя часть окна лежит ПОД
        // системной панелью навигации, и нарисованная там панель героев игроку не видна.
        // Высоту панели присылает сам VK, вычитаем её из рабочей области.
        return height - VkInsets.getBottomCss(height);
    }

    private static shouldFillVkLayoutViewport(): boolean {
        const host = getIntegrationHost();
        if (!host.includes('vk.zazer') && !host.includes('vk.lgtest')) {
            return false;
        }
        const ua = typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent : '';
        return !/iPad|iPhone/i.test(ua);
    }
}
